import type { DataGroup } from '@/features/DataGrid';
import {
  DataTable,
  DimensionTableHeader,
  isTreeDataGrid,
  MetricTableHeader,
  TreeDataGrid,
} from '@/features/DataGrid';
import { useDataFetcher } from '@/shared/hooks';
import { useAppSelector } from '@/shared/model';
import type { MinMaxData, TotalData } from '@/shared/ui';
import { ReadonlyColumnHeader } from '@/shared/ui';
import { selectMetaConfig } from '../../model/slice';
import type {
  DataConfig,
  MarketingPerformanceDataPage,
  TreeDataRow,
} from '../../model/types';

interface ReorderInfo {
  id: string;
  oldIndex: number;
  newIndex: number;
}

interface Props {
  config: DataConfig;
  onGetMinMax: () => Promise<MinMaxData>;
  onGetTotal: () => Promise<TotalData>;
  onGetRows: (offset?: number) => Promise<MarketingPerformanceDataPage>;
  onGetTreeData: (dataGroups: DataGroup[]) => Promise<TreeDataRow[]>;
  onReorderDimensions: (info: ReorderInfo) => void;
  onReorderMetrics: (info: ReorderInfo) => void;
  onColumnsWidthChange: (columnName: string, newWidth?: number) => void;
  isReadonly?: boolean;
}

export function DataGrid({
  config,
  onGetMinMax,
  onGetTotal,
  onGetRows,
  onGetTreeData,
  onReorderDimensions,
  onReorderMetrics,
  onColumnsWidthChange,
  isReadonly,
}: Props) {
  const metaColumnsConfig = useAppSelector(selectMetaConfig);
  const { dataGridViewType, columnsWidth, dimensions, metrics, rowHeight } =
    config;
  const metricsColoring = config.metricsColoring || [];
  const { data: minMax } = useDataFetcher(onGetMinMax);

  const DimensionHeader = isReadonly
    ? ReadonlyColumnHeader
    : DimensionTableHeader;
  const MetricHeader = isReadonly ? ReadonlyColumnHeader : MetricTableHeader;

  if (isTreeDataGrid(dataGridViewType)) {
    return (
      <TreeDataGrid
        columnsWidth={columnsWidth}
        columnsConfig={metaColumnsConfig}
        dimensions={dimensions}
        metrics={metrics}
        rowHeight={rowHeight}
        minMax={minMax}
        metricsColoring={metricsColoring}
        reorderMetrics={onReorderMetrics}
        getData={onGetTreeData}
        getTotal={onGetTotal}
        updateColumnsWidth={onColumnsWidthChange}
        MetricHeaderComponent={MetricHeader}
        disableColumnReorder={isReadonly}
      />
    );
  }

  return (
    <DataTable
      columnsWidth={columnsWidth}
      columnsConfig={metaColumnsConfig}
      dimensions={dimensions}
      metrics={metrics}
      rowHeight={rowHeight}
      minMax={minMax}
      metricsColoring={metricsColoring}
      getRowsPage={onGetRows}
      getTotal={onGetTotal}
      reorderDimensions={onReorderDimensions}
      reorderMetrics={onReorderMetrics}
      updateColumnsWidth={onColumnsWidthChange}
      DimensionHeaderComponent={DimensionHeader}
      MetricHeaderComponent={MetricHeader}
      disableColumnReorder={isReadonly}
    />
  );
}
